import type {
  BucketDocument,
  BucketDocumentDraft,
  BucketEntity,
  BucketEntityDraft
} from '@oysterjs/smartfill-contracts';
import { MAX_DOCUMENT_NAME_LENGTH } from '@oysterjs/smartfill-contracts';
import type { PropertyDraft } from './document';

// Document mutation helpers. Every edit sends the complete replacement
// document: kept nodes preserve their IDs, new nodes omit them so the server
// mints identities (see ReconcileBucketDocument on the backend).

function cloneEntity(entity: BucketEntity): BucketEntityDraft {
  return {
    id: entity.id,
    ...(entity.relation ? { relation: entity.relation } : {}),
    type: entity.type,
    ...(entity.role ? { role: entity.role } : {}),
    ...(entity.label ? { label: entity.label } : {}),
    ...(entity.properties
      ? { properties: entity.properties.map((property) => ({ ...property })) }
      : {}),
    ...(entity.children ? { children: entity.children.map(cloneEntity) } : {})
  };
}

function mapEntity(
  entity: BucketEntityDraft,
  transform: (entity: BucketEntityDraft) => BucketEntityDraft
): BucketEntityDraft {
  const transformed = transform(entity);
  return {
    ...transformed,
    ...(transformed.children
      ? { children: transformed.children.map((child) => mapEntity(child, transform)) }
      : {})
  };
}

/** A deep copy of a document as an editable draft that keeps every node's ID. */
export function toDraft(document: BucketDocument): BucketDocumentDraft {
  return { version: 1, root: cloneEntity(document.root) };
}

export function upsertProperty(
  document: BucketDocument,
  draft: PropertyDraft
): BucketDocumentDraft {
  const result = toDraft(document);
  return {
    version: 1,
    root: mapEntity(result.root, (entity) => {
      if (entity.id !== draft.entityId) return entity;
      const properties = [...(entity.properties ?? [])];
      if (draft.propertyId === null) {
        const usedSemanticKeys = new Set(properties.map((property) => property.semanticKey));
        properties.push({
          semanticKey: uniqueSemanticKey(slugName(draft.displayLabel), usedSemanticKeys),
          displayLabel: draft.displayLabel,
          kind: draft.kind,
          value: draft.value
        });
      } else {
        const index = properties.findIndex((property) => property.id === draft.propertyId);
        if (index >= 0) {
          properties[index] = {
            ...properties[index],
            displayLabel: draft.displayLabel,
            kind: draft.kind,
            value: draft.value
          };
        }
      }
      return { ...entity, properties };
    })
  };
}

export function deleteProperty(
  document: BucketDocument,
  entityId: string,
  propertyId: string
): BucketDocumentDraft {
  const result = toDraft(document);
  return {
    version: 1,
    root: mapEntity(result.root, (entity) => {
      if (entity.id !== entityId) return entity;
      const properties = (entity.properties ?? []).filter((property) => property.id !== propertyId);
      return { ...entity, ...(properties.length > 0 ? { properties } : { properties: undefined }) };
    })
  };
}

export function appendChildEntity(
  document: BucketDocument,
  parentEntityId: string,
  label: string
): BucketDocumentDraft {
  const result = toDraft(document);
  return {
    version: 1,
    root: mapEntity(result.root, (entity) => {
      if (entity.id !== parentEntityId) return entity;
      const children = [...(entity.children ?? [])];
      children.push({
        relation: slugName(label) || 'sections',
        type: 'section',
        label
      });
      return { ...entity, children };
    })
  };
}

export function deleteEntity(document: BucketDocument, entityId: string): BucketDocumentDraft {
  const result = toDraft(document);
  return {
    version: 1,
    root: mapEntity(result.root, (entity) => {
      if (!entity.children?.some((child) => child.id === entityId)) return entity;
      const children = entity.children.filter((child) => child.id !== entityId);
      return { ...entity, ...(children.length > 0 ? { children } : { children: undefined }) };
    })
  };
}

/** Lowercase snake_case machine name, capped at the document name limit. */
export function slugName(value: string, maxLength = MAX_DOCUMENT_NAME_LENGTH): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, maxLength);
}

export function uniqueSemanticKey(semanticKey: string, used: Set<string>): string {
  const base = semanticKey || 'field';
  let candidate = base;
  for (let suffix = 2; used.has(candidate); suffix++) {
    candidate = `${base}_${suffix}`;
  }
  return candidate;
}
